import { inject } from '@angular/core';
import { CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { AuthService, DonorGivingService, FundService } from '@gs-2.0/core';

export const authenticatedRedirectGuard: CanActivateFn = async (
  _,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const fundService = inject(FundService);
  const donorGivingService = inject(DonorGivingService);

  const hasConfirm = state.url.includes('/confirm');
  const fromGiveAsGuest = state.url.includes('giveAsGuest=true');

  // Anonymous, post-payment confirm, and explicit guest flows are never
  // redirected — they belong on the public donate page.
  // GS-only users (no portal access) likewise have no fund dashboard to land on,
  // so they are treated as guests on the public donate page instead.
  if (
    !authService.hasValidToken() ||
    hasConfirm ||
    fromGiveAsGuest ||
    !authService.hasPortalAccess()
  ) {
    return true;
  }

  // Funds drive the DAF check below; load them (in-flight deduped) before deciding.
  // On failure, fall through to the public donate page rather than blocking the route.
  try {
    await fundService.loadFunds();
  } catch {
    return true;
  }

  // DAF users (portal user with an active Foundation Fund) granting via a
  // give-link QR code should land on the Grants page pre-populated with the
  // scanned charity, mirroring the in-app "Give" card-click flow. The give-link
  // URL carries the charity hash as the `id` query param (see donate-form.model).
  //
  // Gate on getFirstActiveFoundationFundId() (not hasAccessibleFoundationFund()):
  // navigateToGrantPage pre-selects that exact fund, and the downstream
  // fullAccessGuard requires a selected fund. The two predicates differ only for
  // EXPIRED DAF funds — gating here on the active-fund id ensures an expired-only
  // DAF user falls through to /public/donate instead of dead-ending on the
  // dashboard when no fund can be pre-selected.
  //
  // The active-fund id is the sole signal here: it already encodes DAF type +
  // Full Access + not-expired, which is the true granting eligibility. We do NOT
  // additionally gate on hasPortalUserRole() — the early-return above already
  // guarantees hasPortalAccess(), so PORTAL_ADMIN-only fund holders (who lack the
  // PORTAL_USER role but are legitimate DAF grantors) are correctly redirected.
  if (fundService.getFirstActiveFoundationFundId() !== null) {
    const charityHash =
      new URL(state.url, window.location.origin).searchParams.get('id') ?? undefined;
    // navigateToGrantPage pre-selects the first active Foundation Fund, which the
    // downstream fullAccessGuard on /fund/grant requires (it dereferences the
    // selected fund). It navigates imperatively, so we cancel this activation.
    await donorGivingService.navigateToGrantPage(undefined, charityHash, 'charity', undefined);
    return false;
  }

  // Non-DAF portal users (e.g. portal admins, or users with only a Charity/Project
  // fund) cannot grant — let them through to the public donate page as a donor.
  return true;
};
